<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_TEAMS, INITIAL_PLAYERS, INITIAL_EVENTS } from '@/services/mockData';
import { useToastNotification } from '@/composables/useToastNotification';
import AppBadge from '@/components/ui/AppBadge.vue';
import { NCard, NTag, NButton } from 'naive-ui';
import {
  Heart,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
  Navigation,
  ShieldCheck
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant } = useTenant();
const { notifySuccess } = useToastNotification();

const myAthlete = computed(() => INITIAL_PLAYERS[0]); // Cole Vance #24
const myTeam = computed(() => INITIAL_TEAMS[0]);
const nextEvent = computed(() => INITIAL_EVENTS[0]);

const handleConfirmAttendance = () => {
  notifySuccess(
    t('parentDashboard.rsvpSuccess'),
    t('parentDashboard.rsvpSuccessMsg', { name: myAthlete.value.firstName, event: nextEvent.value.title })
  );
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Parent Banner -->
    <div class="p-6 rounded-3xl bg-gradient-to-r from-emerald-950 via-slate-900 to-slate-900 border border-emerald-500/20 shadow-2xl">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <div class="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
            <Heart class="w-7 h-7" />
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h1 class="text-2xl font-extrabold text-white tracking-tight">
                {{ t('parentDashboard.title', { name: `${myAthlete.lastName} ${myAthlete.firstName}` }) }}
              </h1>
              <AppBadge variant="success">#{{ myAthlete.jerseyNumber }} {{ t('common.active') }}</AppBadge>
            </div>
            <p class="text-xs text-slate-300 mt-0.5">
              {{ t('parentDashboard.enrolledWith', { team: myTeam.name, org: activeTenant.name }) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <NTag type="success" round size="medium" :bordered="false">
            {{ t('parentDashboard.duesPaid') }}
          </NTag>
        </div>
      </div>
    </div>

    <!-- Logistics Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <!-- Game Day Travel & Details (7 cols) -->
      <div class="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-5">
        <div class="flex items-center justify-between pb-3 border-b border-slate-800">
          <div class="flex items-center gap-2">
            <Navigation class="w-4 h-4 text-emerald-400" />
            <h2 class="text-sm font-bold uppercase tracking-wider text-slate-100">{{ t('parentDashboard.gameDayDest') }}</h2>
          </div>
          <span class="text-xs font-semibold text-slate-400">{{ t('parentDashboard.round1') }}</span>
        </div>

        <div>
          <h3 class="text-lg font-extrabold text-slate-100">{{ nextEvent.title }}</h3>
          <p class="text-xs text-slate-400 mt-0.5">VS: <strong class="text-slate-200">{{ nextEvent.opponent }}</strong></p>
        </div>

        <div class="p-4 bg-slate-950 rounded-2xl border border-slate-800 space-y-3">
          <div class="flex items-center justify-between text-xs">
            <span class="text-slate-400">{{ t('parentDashboard.complexAddress') }}</span>
            <span class="font-semibold text-slate-200">{{ nextEvent.location }}</span>
          </div>
          <div class="flex items-center justify-between text-xs">
            <span class="text-slate-400">{{ t('parentDashboard.dropOff') }}</span>
            <span class="font-bold text-sky-400">{{ t('parentDashboard.dropOffVal') }}</span>
          </div>
          <div class="flex items-center justify-between text-xs">
            <span class="text-slate-400">{{ t('parentDashboard.gameUniform') }}</span>
            <span class="font-bold text-amber-300">{{ nextEvent.uniformColor }}</span>
          </div>
        </div>

        <!-- Parent RSVP Action -->
        <div class="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="flex items-center gap-2.5">
            <CheckCircle2 class="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <p class="text-xs font-bold text-emerald-300">{{ t('parentDashboard.transportConfirmed') }}</p>
              <p class="text-[11px] text-slate-400">{{ t('parentDashboard.athleteMarked', { name: myAthlete.firstName }) }}</p>
            </div>
          </div>
          <button
            type="button"
            @click="handleConfirmAttendance"
            class="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs rounded-xl transition self-start sm:self-auto"
          >
            {{ t('parentDashboard.update') }}
          </button>
        </div>
      </div>

      <!-- Coach Contact & Team Details (5 cols) -->
      <div class="lg:col-span-5 space-y-4">
        <!-- Direct Coach Contact -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
          <h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider">{{ t('parentDashboard.headCoachContact') }}</h3>
          <div class="p-3.5 bg-slate-950 rounded-2xl border border-slate-800 space-y-2.5">
            <p class="text-sm font-bold text-slate-100">{{ myTeam.headCoach }}</p>
            <div class="flex items-center gap-2 text-xs text-slate-300">
              <Mail class="w-3.5 h-3.5 text-slate-500" />
              <span>m.holloway@diamondacademy.com</span>
            </div>
            <div class="flex items-center gap-2 text-xs text-slate-300">
              <Phone class="w-3.5 h-3.5 text-slate-500" />
              <span>(555) 849-2041</span>
            </div>
          </div>
        </div>

        <!-- Emergency / Medical Card -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
          <div class="flex items-center justify-between">
            <h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <ShieldCheck class="w-4 h-4 text-emerald-400" />
              {{ t('parentDashboard.medWaiver') }}
            </h3>
            <AppBadge variant="success">{{ t('parentDashboard.cleared') }}</AppBadge>
          </div>
          <p class="text-xs text-slate-400">
            {{ t('parentDashboard.waiverMsg') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>